import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, CheckCircle, FileText, ArrowRight, Truck, Shield, Target, Wind, Users, Scale, Map } from 'lucide-react';

const MiningConstruction = () => {
  const servicePillars = [
    {
      title: 'Major Mine PSD Strategy',
      description: 'Strategies developed through direct oversight of Alaska\'s most complex Title I evaluations. We design Air Quality Modeling Protocols that anticipate agency questions, preventing the costly "do-loop" of rejection. Complex terrain dispersion modeling (AERMOD) calibrated to pass State review.',
      icon: Shield
    },
    {
      title: 'Fugitive Dust & PM Defense',
      description: 'Protecting your social license to operate. We develop Fugitive Dust Control Plans built to withstand the scrutiny of an ADEC inspection—covering tailings management, haul road emissions, and crushing operations with practical, enforceable controls.',
      icon: Wind
    },
    {
      title: 'Rapid-Response Road Compliance',
      description: 'Alaska General Permits (MG-3 & MG-9) for asphalt plants and rock crushers. We understand that for road construction, timing is everything. Fast-track permit acquisition so your materials stay mobile and your schedule stays intact.',
      icon: Truck
    },
    {
      title: 'Operational Mine Support',
      description: 'Drawing on direct regulatory oversight of Alaska\'s major mines (Greens Creek, Kensington, POGO), we build compliance strategies that align with the practical realities of active sites—ensuring air permits flex with shifting mine plans.',
      icon: Target
    },
    {
      title: 'NSR Avoidance & Synthetic Minors',
      description: 'Strategic permit engineering to maintain minor source status. We help operators implement Owner Requested Limits (ORLs) that reduce Title V administrative burden while preserving maximum operational capacity.',
      icon: Scale
    },
    {
      title: 'Agency Negotiation & NEPA',
      description: 'Permit conditions negotiated through fluency in Agency priorities and constraints. When we represent clients during NEPA reviews, we speak the internal language of regulatory decision-making—ensuring requirements are practical, defensible, and achievable in Alaska\'s field conditions.',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen" data-testid="mining-construction-page">
      {/* Hero Section */}
      <section
        className="hero-section relative text-white overflow-hidden"
        style={{
          height: '60vh',
          maxHeight: '720px',
          minHeight: '420px',
          padding: '5rem 0'
        }}
      >
        {/* Responsive Hero Image Container */}
        <div className="absolute inset-0 z-0">
          <style>{`
            .hero-section .hero-image,
            .hero-section .hero-image img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center !important;
              display: block;
            }
            
            @media (max-width: 900px) {
              .hero-section {
                height: auto !important;
                max-height: none !important;
                padding: 3rem 0 !important;
              }
            }
          `}</style>
          
          <picture className="hero-image">
            {/* Mobile Vertical (Default Mobile Portrait) */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/08ydf738_mining_hero_mobile_vertical_960x1200.webp"
              media="(max-width: 480px)" />

            {/* Mobile Horizontal */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/bns3h37a_mining_hero_mobile_horizontal_960x640.webp"
              media="(max-width: 767px)" />

            {/* Tablet */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/qywfddus_mining_hero_tablet_1536x900.webp"
              media="(max-width: 1199px)" />

            {/* Desktop */}
            <img
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/lml4v53w_mining_hero_desktop_2560x1200.webp"
              alt="Open-pit mining operation in Alaska at sunset, with a large haul truck, mining excavator, and layered mine walls—representing environmental consulting for mining and road construction projects."
              loading="eager"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
          </picture>
          
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">
              Precision Permitting for Alaska's Mining Frontier
            </h1>
            <p className="text-xl md:text-2xl mb-8 drop-shadow-md leading-relaxed">
              Navigating Title I and Title V requirements demands fluency in the regulator's internal logic—not just the written rules. We apply the Agency's evaluation standards to your application before submission, anticipating the questions that trigger rejection cycles. Because understanding compliance isn't enough. You need to understand interpretation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg"
              >
                De-Risk Your Project
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Escaping Modeling Purgatory</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              For major mining projects, the danger isn't just permit denial—it's delay. Years spent in the "do-loop" of protocol rejection, each revision costing mobilization windows and investor confidence. The difference between approval and purgatory often lies in regulatory calibration: knowing which technical arguments the Agency will accept and which will trigger another round.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Raven Environmental's methodology carries the institutional DNA of Alaska's permitting system. Our strategies were forged through direct oversight of the State's most complex Title I evaluations. We know why permits get stuck in review cycles—because we used to be the ones sending them back. That institutional memory now works in reverse: pre-emptive scrutiny applied to your application before the Agency ever sees it.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              The result: permits engineered to withstand agency review, public comment periods, and legal challenge. Not because we write better technical reports—but because we know exactly how those reports will be evaluated.
            </p>
          </div>
        </div>
      </section>

      {/* Core Service Pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Core Service Pillars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicePillars.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-orange-600">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center mr-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Technical Specifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Box 1: Major Mining / PSD Framework */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-orange-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Major Mining / PSD Framework</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PSD Permitting & Increment Analysis Strategy</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">BACT (Best Available Control Technology) Defense</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Complex Terrain Dispersion Modeling (AERMOD)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Title V Operating Permits & Renewals</span>
                </li>
              </ul>
            </div>

            {/* Box 2: Infrastructure & Road Construction */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-orange-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Infrastructure & Road Construction</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">MG-3 (Asphalt Plants) & MG-9 (Rock Crushers)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NSPS Subpart OOO (Nonmetallic Mineral Processing)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NESHAP Subpart JJJJJJ (Industrial Boilers)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Fugitive Particulate Matter (FPM) Plans</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Compliance Solutions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Solution 1 */}
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Crushing & Processing Compliance</h3>
              <p className="text-gray-700 mb-3">NSPS Subpart OOO (Nonmetallic Minerals)</p>
              <p className="text-sm text-gray-600">Rock crushers, screens, and conveyors—ensuring particulate controls meet federal standards.</p>
            </div>

            {/* Solution 2 */}
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Asphalt Plant Standards</h3>
              <p className="text-gray-700 mb-3">NSPS Subpart I (Hot Mix Asphalt)</p>
              <p className="text-sm text-gray-600">Emission limits and opacity requirements for batch and drum mix plants.</p>
            </div>

            {/* Solution 3 */}
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Engine Compliance (Diesel & Gas)</h3>
              <p className="text-gray-700 mb-3">NSPS Subparts IIII & JJJJ</p>
              <p className="text-sm text-gray-600">Certification and emission standards for compression ignition (CI) and spark ignition (SI) engines.</p>
            </div>

            {/* Solution 4 */}
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Industrial Boiler Strategy</h3>
              <p className="text-gray-700 mb-3">NESHAP Subpart JJJJJJ</p>
              <p className="text-sm text-gray-600">Tune-ups, energy assessments, and emission limits for mine camp heating systems.</p>
            </div>

            {/* Solution 5 */}
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Metallic Mineral Processing</h3>
              <p className="text-gray-700 mb-3">NSPS Subpart LL</p>
              <p className="text-sm text-gray-600">Ore processing, dryers, and material handling for gold, silver, and critical mineral operations.</p>
            </div>

            {/* Solution 6 */}
            <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">PSD & Title V Management</h3>
              <p className="text-gray-700 mb-3">Major Source Permitting</p>
              <p className="text-sm text-gray-600">BACT determinations, increment analysis, and complex terrain modeling for Tier-1 assets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Title I Permits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-orange-50 p-8 rounded-lg border-2 border-orange-200 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-8 h-8 text-orange-600 mr-3" />
              Title I Permit Requirements
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Mining and road construction permittees must comply with comprehensive stationary source-specific requirements:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Best Available Control Technology (BACT) limits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">EPA Prevention of Significant Deterioration (PSD) permits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">State Implementation Plan (SIP)-approved construction and minor permits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Compliance with ambient air quality standards and maximum allowable ambient concentrations</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Owner-requested limits established under 18 AAC 50.225</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Strategic Authority */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategic Authority</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Your permit is the gatekeeper to your production. Work with a consultant who has sat in the decision-maker's chair. Hundreds of permits authored. Billions of dollars in assets regulated. Ready to work for you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-orange-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Apply Regulatory Calibration?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Work with a firm whose methodology was forged inside Alaska's Title I permitting system. We evaluate your project using the Agency's internal decision framework—before you ever submit. De-risk your timeline. Secure your mobilization window.
          </p>
          <Link
            to="/contact/mining-construction"
            className="inline-flex items-center px-8 py-4 bg-white text-orange-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            De-Risk Your Project
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MiningConstruction;
